/**
 * Phase 10e — Modal affichée lorsqu'un POST /manual ou POST Peppol renvoie 409 Conflict
 * (duplicate strict, même tenant, même fournisseur + n° facture).
 *
 * Payload attendu du backend :
 * {
 *   detail: string,
 *   existing_invoice_id: string,
 *   existing_source: "peppol_inbound" | "manual" | "test_fixture",
 *   existing_invoice_number: string,
 *   existing_supplier_name: string,
 *   incoming_source: string,
 *   existing_source_label: string,
 *   incoming_source_label: string,
 * }
 */
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, ExternalLink } from "lucide-react";
import SupplierInvoiceSourceBadge from "@/components/SupplierInvoiceSourceBadge";

export default function DuplicateInvoiceModal({ open, onOpenChange, conflict, onViewExisting }) {
  if (!conflict) return null;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="border-slate-800 bg-slate-900 text-slate-100"
        data-testid="duplicate-invoice-modal"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-300">
            <AlertTriangle className="h-5 w-5" />
            Facture déjà existante
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-slate-400">
              <p>
                Une facture avec le même fournisseur et le même numéro existe déjà pour votre entreprise.
                Impossible d&apos;en créer une seconde (règle de dédup stricte).
              </p>
              <div className="rounded border border-slate-800 bg-slate-950/50 p-3">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <SupplierInvoiceSourceBadge source={conflict.existing_source} />
                  <span className="text-xs text-slate-500">Facture existante</span>
                </div>
                <div className="grid gap-1 text-sm text-slate-200">
                  <div>
                    <span className="text-slate-500 text-xs">Fournisseur : </span>
                    <span className="font-medium">{conflict.existing_supplier_name || "—"}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 text-xs">N° facture : </span>
                    <span className="font-mono">{conflict.existing_invoice_number || "—"}</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Consultez la facture existante pour vérifier son statut, ou modifiez votre saisie
                (N° facture, VAT) si vous pensez qu&apos;il s&apos;agit d&apos;une erreur.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            className="border-slate-700 bg-slate-800 text-slate-200"
            data-testid="duplicate-modal-cancel"
          >
            Fermer
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onViewExisting?.(conflict.existing_invoice_id)}
            className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
            data-testid="duplicate-modal-view-existing"
          >
            <ExternalLink className="mr-1 h-4 w-4" /> Voir la facture existante
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
